import { useStaticQuery, graphql } from "gatsby"

import { useCallback, useMemo } from "react"

interface ConsolidatedItemNode {
  storyblokUuid: string
  full_slug: string
  isInternal: boolean
  item: unknown
}

export type ConsolidatedItemEntry = {
  item: YocoCom.ConsolidatedItem
  isInternal: boolean
  fullSlug: string
}

const isConsolidatedItemNode = (
  item: unknown
): item is YocoCom.ConsolidatedItem => {
  if (typeof item !== "object" || item === null) {
    return false
  }

  const candidate = item as Record<string, unknown>

  if (
    typeof candidate.storyblokUuid !== "string" ||
    typeof candidate.name !== "string"
  ) {
    return false
  }

  if (candidate.type === "onlineProduct") {
    return true
  }

  return (
    (candidate.type === "product" || candidate.type === "bundle") &&
    typeof candidate.price === "string"
  )
}

const useConsolidatedItemNodeData = () =>
  useStaticQuery<{
    allConsolidatedItem: { nodes: ConsolidatedItemNode[] }
  }>(graphql`
    query ConsolidatedItems {
      allConsolidatedItem {
        nodes {
          storyblokUuid
          full_slug
          isInternal
          item
        }
      }
    }
  `)

const useConsolidatedItemsByUuid = () => {
  const data = useConsolidatedItemNodeData()

  return useMemo(() => {
    const map = new Map<string, YocoCom.ConsolidatedItem>()

    for (const node of data.allConsolidatedItem.nodes) {
      if (isConsolidatedItemNode(node.item)) {
        map.set(node.storyblokUuid, node.item)
      }
    }

    return map
  }, [data])
}

export const useConsolidatedItemEntries = (): ConsolidatedItemEntry[] => {
  const data = useConsolidatedItemNodeData()

  return useMemo(
    () =>
      data.allConsolidatedItem.nodes.flatMap((node) =>
        isConsolidatedItemNode(node.item)
          ? [
              {
                item: node.item,
                isInternal: node.isInternal === true,
                fullSlug: node.full_slug,
              },
            ]
          : []
      ),
    [data]
  )
}

export const useGetConsolidatedItemNode = () => {
  const itemsByUuid = useConsolidatedItemsByUuid()

  return useCallback(
    (storyblokUuid: string): YocoCom.ConsolidatedItem | undefined =>
      itemsByUuid.get(storyblokUuid),
    [itemsByUuid]
  )
}
